import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { useDateLocale } from "@/hooks/useDateLocale";
import { Briefcase, Download, MapPin, Mail, Phone, Plus, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AddCandidateDialog } from "@/components/recruitment/AddCandidateDialog";
import { useAuth } from "@/contexts/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  apiGetRecruitmentPositions,
  apiGetRecruitmentApplications,
  apiGetRecruitmentApplication,
  apiUpdateRecruitmentApplicationStatus,
  apiDownloadRecruitmentResume,
  apiGetRecruitmentDashboardStats,
  type RecruitmentApplication,
  type RecruitmentPosition,
} from "@/lib/api";

function getDeadlineDaysLeft(deadline: string): number {
  const d = new Date(deadline);
  d.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((d.getTime() - today.getTime()) / 86400000);
}

function MobileDeadlineBadge({ deadline }: { deadline: string }) {
  const { t } = useTranslation();
  const daysLeft = getDeadlineDaysLeft(deadline);
  const formatted = new Date(deadline).toLocaleDateString();

  if (daysLeft < 0) {
    return <Badge variant="outline" className="text-[10px] shrink-0 bg-red-500/10 text-red-600 border-red-500/20">{t("recruitment.dashboard.expired")}</Badge>;
  }
  if (daysLeft <= 3) {
    return <Badge variant="outline" className="text-[10px] shrink-0 bg-amber-500/10 text-amber-600 border-amber-500/20">{t("recruitment.dashboard.closingSoon")}</Badge>;
  }
  return <Badge variant="outline" className="text-[10px] shrink-0">{t("recruitment.dashboard.closesOn", { date: formatted })}</Badge>;
}

const APP_STATUS_VALUES = ["new", "screening", "interview", "offer", "hired", "rejected", "withdrawn"];

const STATUS_COLORS: Record<string, string> = {
  draft: "bg-slate-500/10 text-slate-600 border-slate-500/20",
  open: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20",
  on_hold: "bg-amber-500/10 text-amber-600 border-amber-500/20",
  closed: "bg-gray-500/10 text-gray-600 border-gray-500/20",
  new: "bg-blue-500/10 text-blue-600 border-blue-500/20",
  screening: "bg-amber-500/10 text-amber-600 border-amber-500/20",
  interview: "bg-violet-500/10 text-violet-600 border-violet-500/20",
  offer: "bg-cyan-500/10 text-cyan-600 border-cyan-500/20",
  hired: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20",
  rejected: "bg-red-500/10 text-red-600 border-red-500/20",
  withdrawn: "bg-gray-500/10 text-gray-600 border-gray-500/20",
};

export default function MobileRecruitment() {
  const { t } = useTranslation();
  const dateLocale = useDateLocale();
  const { user } = useAuth();
  const canManuallyAddCandidate = user?.role.mainRole === "hr" || user?.role.mainRole === "admin";
  const qc = useQueryClient();
  const [selectedApp, setSelectedApp] = useState<RecruitmentApplication | null>(null);
  const [selectedPosition, setSelectedPosition] = useState<RecruitmentPosition | null>(null);
  const [addCandidateOpen, setAddCandidateOpen] = useState(false);

  const { data: positions = [] } = useQuery({
    queryKey: ["mobile-recruitment-positions"],
    queryFn: apiGetRecruitmentPositions,
    staleTime: 60_000,
  });

  const { data: stats } = useQuery({
    queryKey: ["mobile-recruitment-dashboard-stats"],
    queryFn: apiGetRecruitmentDashboardStats,
    staleTime: 60_000,
  });

  const { data: applications = [] } = useQuery({
    queryKey: ["mobile-recruitment-applications"],
    queryFn: () => apiGetRecruitmentApplications(),
    staleTime: 60_000,
  });

  const { data: appDetail } = useQuery({
    queryKey: ["mobile-recruitment-application", selectedApp?.id],
    queryFn: () => apiGetRecruitmentApplication(selectedApp!.id),
    enabled: !!selectedApp,
  });

  const statusMutation = useMutation({
    mutationFn: ({ id, status }: { id: number; status: string }) =>
      apiUpdateRecruitmentApplicationStatus(id, { status }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["mobile-recruitment-applications"] });
      toast.success(t("mobile.recruitment.statusUpdated"));
    },
    onError: () => toast.error(t("mobile.teamLeave.actionFailed")),
  });

  return (
    <div className="flex flex-col h-full">
      {stats && (
        <div className="grid grid-cols-3 gap-2 px-4 pt-4 shrink-0">
          <div className="rounded-xl border border-border/60 bg-card p-2.5 text-center">
            <p className="text-lg font-bold text-foreground">{stats.totalOpenPositions}</p>
            <p className="text-[10px] text-muted-foreground leading-tight">{t("recruitment.dashboard.openPositions")}</p>
          </div>
          <div className="rounded-xl border border-border/60 bg-card p-2.5 text-center">
            <p className="text-lg font-bold text-foreground">{stats.totalCandidates}</p>
            <p className="text-[10px] text-muted-foreground leading-tight">{t("recruitment.dashboard.totalCandidates")}</p>
          </div>
          <div className="rounded-xl border border-border/60 bg-card p-2.5 text-center">
            <p className="text-lg font-bold text-foreground">{stats.positionsClosingSoon.length}</p>
            <p className="text-[10px] text-muted-foreground leading-tight">{t("recruitment.dashboard.closingSoon")}</p>
          </div>
        </div>
      )}
      {canManuallyAddCandidate && (
        <div className="px-4 pt-3 shrink-0">
          <Button className="w-full" onClick={() => setAddCandidateOpen(true)}>
            <Plus className="h-4 w-4 mr-1.5" />
            {t("recruitment.applications.manual.add")}
          </Button>
        </div>
      )}
      <Tabs defaultValue="pipeline" className="flex flex-col flex-1 overflow-hidden">
        <div className="px-4 pt-4 pb-2 shrink-0">
          <TabsList className="grid grid-cols-2 w-full">
            <TabsTrigger value="pipeline" className="text-xs">
              <Users className="h-3.5 w-3.5 mr-1.5" />
              {t("mobile.recruitment.pipeline")}
            </TabsTrigger>
            <TabsTrigger value="positions" className="text-xs">
              <Briefcase className="h-3.5 w-3.5 mr-1.5" />
              {t("mobile.recruitment.positions")}
            </TabsTrigger>
          </TabsList>
        </div>

        {/* Pipeline tab */}
        <TabsContent value="pipeline" className="flex-1 overflow-y-auto mt-0 px-4 pb-4">
          {applications.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-16 text-muted-foreground">
              <Users className="h-10 w-10 opacity-30" />
              <p className="text-sm">{t("mobile.recruitment.noApplications")}</p>
            </div>
          ) : (
            <div className="bg-card border border-border/60 rounded-xl overflow-hidden mt-2">
              {applications.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => setSelectedApp(a)}
                  className="w-full flex items-center gap-3 px-4 py-3 border-b border-border/30 last:border-0 hover:bg-muted/40 active:bg-muted/60 transition-colors text-left"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">
                      {a.firstName} {a.lastName}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">
                      {a.positionTitle ?? "—"} · {format(parseISO(a.appliedAt), "d MMM yyyy", { locale: dateLocale })}
                    </p>
                  </div>
                  <Badge variant="outline" className={`text-[10px] shrink-0 capitalize ${STATUS_COLORS[a.status] ?? ""}`}>
                    {t(`recruitment.applications.statuses.${a.status}`, { defaultValue: a.status })}
                  </Badge>
                </button>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Positions tab */}
        <TabsContent value="positions" className="flex-1 overflow-y-auto mt-0 px-4 pb-4">
          {positions.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-16 text-muted-foreground">
              <Briefcase className="h-10 w-10 opacity-30" />
              <p className="text-sm">{t("mobile.recruitment.noPositions")}</p>
            </div>
          ) : (
            <div className="bg-card border border-border/60 rounded-xl overflow-hidden mt-2">
              {positions.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setSelectedPosition(p)}
                  className="w-full flex items-center gap-3 px-4 py-3 border-b border-border/30 last:border-0 hover:bg-muted/40 active:bg-muted/60 transition-colors text-left"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{p.title}</p>
                    <p className="text-xs text-muted-foreground truncate">
                      {p.departmentName ?? "—"} · {p.applicationCount} {t("mobile.recruitment.applicants")}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    <Badge variant="outline" className={`text-[10px] shrink-0 capitalize ${STATUS_COLORS[p.status] ?? ""}`}>
                      {p.status.replace("_", " ")}
                    </Badge>
                    {p.applicationDeadline && <MobileDeadlineBadge deadline={p.applicationDeadline} />}
                  </div>
                </button>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Application detail sheet */}
      <Sheet open={!!selectedApp} onOpenChange={(open) => { if (!open) setSelectedApp(null); }}>
        <SheetContent side="bottom" className="rounded-t-2xl max-h-[85vh] overflow-y-auto">
          {selectedApp && (
            <>
              <SheetHeader className="text-left">
                <SheetTitle>{selectedApp.firstName} {selectedApp.lastName}</SheetTitle>
              </SheetHeader>

              <div className="space-y-3 mt-4">
                <p className="text-sm text-muted-foreground">{selectedApp.positionTitle}</p>

                <a href={`mailto:${selectedApp.email}`} className="flex items-center gap-2 text-sm text-primary">
                  <Mail className="h-4 w-4" />
                  {selectedApp.email}
                </a>
                {selectedApp.phone && (
                  <a href={`tel:${selectedApp.phone}`} className="flex items-center gap-2 text-sm text-primary">
                    <Phone className="h-4 w-4" />
                    {selectedApp.phone}
                  </a>
                )}
                {selectedApp.resumeFileKey && (
                  <button
                    type="button"
                    onClick={() => apiDownloadRecruitmentResume(selectedApp.id)}
                    className="flex items-center gap-2 text-sm text-primary"
                  >
                    <Download className="h-4 w-4" />
                    {t("mobile.recruitment.downloadResume")}
                  </button>
                )}
                {selectedApp.coverLetter && (
                  <div className="text-sm">
                    <p className="text-muted-foreground mb-1">{t("mobile.recruitment.coverLetter")}</p>
                    <p className="text-foreground/90 whitespace-pre-wrap">{selectedApp.coverLetter}</p>
                  </div>
                )}
                {appDetail?.notes && (
                  <div className="text-sm">
                    <p className="text-muted-foreground mb-1">{t("mobile.recruitment.notes")}</p>
                    <p className="text-foreground/90 whitespace-pre-wrap">{appDetail.notes}</p>
                  </div>
                )}
              </div>

              <div className="mt-5">
                <p className="text-xs text-muted-foreground mb-1.5">{t("mobile.recruitment.changeStatus")}</p>
                <Select
                  value={selectedApp.status}
                  onValueChange={(status) => {
                    statusMutation.mutate({ id: selectedApp.id, status });
                    setSelectedApp({ ...selectedApp, status });
                  }}
                >
                  <SelectTrigger className="h-11">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {APP_STATUS_VALUES.map((s) => (
                      <SelectItem key={s} value={s} className="capitalize">
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {canManuallyAddCandidate && (
        <AddCandidateDialog
          open={addCandidateOpen}
          onOpenChange={setAddCandidateOpen}
          positions={positions}
          onCreated={(application) => {
            qc.invalidateQueries({ queryKey: ["mobile-recruitment-applications"] });
            qc.invalidateQueries({ queryKey: ["mobile-recruitment-dashboard-stats"] });
            setSelectedApp(application);
          }}
        />
      )}

      {/* Position detail sheet */}
      <Sheet open={!!selectedPosition} onOpenChange={(open) => { if (!open) setSelectedPosition(null); }}>
        <SheetContent side="bottom" className="rounded-t-2xl max-h-[85vh] overflow-y-auto">
          {selectedPosition && (
            <>
              <SheetHeader className="text-left">
                <SheetTitle>{selectedPosition.title}</SheetTitle>
              </SheetHeader>

              <div className="space-y-3 mt-4">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant="outline" className={`text-[10px] capitalize ${STATUS_COLORS[selectedPosition.status] ?? ""}`}>
                    {selectedPosition.status.replace("_", " ")}
                  </Badge>
                  <Badge variant="outline" className="text-[10px] capitalize">
                    {selectedPosition.jobType.replace("_", " ")}
                  </Badge>
                </div>

                {selectedPosition.departmentName && (
                  <div className="flex items-center gap-2 text-sm text-foreground/90">
                    <Briefcase className="h-4 w-4 text-muted-foreground shrink-0" />
                    {selectedPosition.departmentName}
                  </div>
                )}
                {selectedPosition.location && (
                  <div className="flex items-center gap-2 text-sm text-foreground/90">
                    <MapPin className="h-4 w-4 text-muted-foreground shrink-0" />
                    {selectedPosition.location}
                  </div>
                )}
                <div className="flex items-center gap-2 text-sm text-foreground/90">
                  <Users className="h-4 w-4 text-muted-foreground shrink-0" />
                  {selectedPosition.applicationCount} {t("mobile.recruitment.applicants")}
                </div>

                {selectedPosition.description && (
                  <div className="text-sm">
                    <p className="text-muted-foreground mb-1">{t("mobile.recruitment.description")}</p>
                    <p className="text-foreground/90 whitespace-pre-wrap">{selectedPosition.description}</p>
                  </div>
                )}
                {selectedPosition.requirements && (
                  <div className="text-sm">
                    <p className="text-muted-foreground mb-1">{t("mobile.recruitment.requirements")}</p>
                    <p className="text-foreground/90 whitespace-pre-wrap">{selectedPosition.requirements}</p>
                  </div>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
